import { useEffect, useRef } from 'react'
import { getCurrentUserProfileRequest, getPublicEnrollmentStateRequest } from '@/api/enrollment-api'
import { useLocation, useNavigate } from '@/router/navigation'
import { useEnrollmentStore } from '@/stores'
import { getAgeFromBirthDate } from '../applicationForm/utils'
import { getEnrollmentApiErrorMessage } from '../../utils/enrollmentApiErrorMessage'
import { getEnrollmentString } from '../../utils/enrollmentHydration/helpers'
import { normalizeCurrentStepForUi } from '../../utils/enrollmentHydration/status'
import {
  normalizeSelfEnrollmentEntryStep,
  SELF_ENROLLMENT_STEPS
} from '../selfEnrollmentFlow/utils/steps'
import { normalizeInternalEnrollmentPath } from './routes'

type UseSponsoredEnrollmentProfileBootstrapArgs = {
  sessionToken: string | null
  onError: (message: string | null) => void
  onLoadingChange: (isLoading: boolean) => void
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const hasCompleteApplicationEntryFields = (profile: Awaited<ReturnType<typeof getCurrentUserProfileRequest>>) => {
  const personalData = profile.personalData

  return (
    isNonEmptyString(profile.firstName) &&
    isNonEmptyString(profile.lastName) &&
    isNonEmptyString(profile.email) &&
    isNonEmptyString(personalData?.phoneNumber) &&
    isNonEmptyString(personalData?.addressLine1) &&
    isNonEmptyString(personalData?.city) &&
    isNonEmptyString(personalData?.state) &&
    isNonEmptyString(personalData?.zipCode)
  )
}

const isUnder18Enrollment = (
  profile: Awaited<ReturnType<typeof getCurrentUserProfileRequest>>,
  enrollmentSession: Awaited<ReturnType<typeof getPublicEnrollmentStateRequest>>
) => {
  const birthDate =
    profile.personalData?.dateOfBirth || getEnrollmentString(enrollmentSession, ['dateOfBirth'])
  const age = getAgeFromBirthDate(birthDate)

  return age !== null && age < 18
}

const isNoLicenseEnrollment = (
  profile: Awaited<ReturnType<typeof getCurrentUserProfileRequest>>,
  enrollmentSession: Awaited<ReturnType<typeof getPublicEnrollmentStateRequest>>
) => {
  const expectedLicenseMonth =
    typeof (profile as Record<string, unknown>).expectedLicenseMonth === 'number'
      ? ((profile as Record<string, unknown>).expectedLicenseMonth as number)
      : typeof (enrollmentSession as Record<string, unknown>).expectedLicenseMonth === 'number'
        ? ((enrollmentSession as Record<string, unknown>).expectedLicenseMonth as number)
        : null
  const expectedLicenseYear =
    typeof (profile as Record<string, unknown>).expectedLicenseYear === 'number'
      ? ((profile as Record<string, unknown>).expectedLicenseYear as number)
      : typeof (enrollmentSession as Record<string, unknown>).expectedLicenseYear === 'number'
        ? ((enrollmentSession as Record<string, unknown>).expectedLicenseYear as number)
        : null

  return (
    typeof expectedLicenseMonth === 'number' &&
    Number.isFinite(expectedLicenseMonth) &&
    typeof expectedLicenseYear === 'number' &&
    Number.isFinite(expectedLicenseYear)
  )
}

export function useSponsoredEnrollmentProfileBootstrap({
  sessionToken,
  onError,
  onLoadingChange
}: UseSponsoredEnrollmentProfileBootstrapArgs) {
  const navigate = useNavigate()
  const location = useLocation()
  const setEnrollmentId = useEnrollmentStore(state => state.setEnrollmentId)
  const setCurrentStep = useEnrollmentStore(state => state.setCurrentStep)
  const setResumedEnrollment = useEnrollmentStore(state => state.setResumedEnrollment)
  const setUserProfile = useEnrollmentStore(state => state.setUserProfile)
  const lastLoadedProfileTokenRef = useRef<string | null>(null)
  const previousActivePathRef = useRef<string | null>(null)
  const activePath = normalizeInternalEnrollmentPath(location.pathname)
  const shouldStayOnApplicationFromNavigation = location.state?.stayOnApplication === true

  useEffect(() => {
    const previousActivePath = previousActivePathRef.current
    previousActivePathRef.current = activePath

    if (!sessionToken || activePath !== '/application') {
      lastLoadedProfileTokenRef.current = null
      onLoadingChange(false)
      return
    }

    if (lastLoadedProfileTokenRef.current === sessionToken) {
      onLoadingChange(false)
      return
    }

    let mounted = true

    const loadUserProfile = async () => {
      onLoadingChange(true)

      try {
        const [profile, enrollmentSession] = await Promise.all([
          getCurrentUserProfileRequest(sessionToken),
          getPublicEnrollmentStateRequest(sessionToken)
        ])

        if (!mounted) {
          return
        }

        setUserProfile(profile)
        setResumedEnrollment(enrollmentSession)
        onError(null)

        if (typeof profile.enrollmentId === 'number') {
          setEnrollmentId(profile.enrollmentId)
        }

        if (isUnder18Enrollment(profile, enrollmentSession)) {
          navigate('/thank-you', {
            replace: true,
            state: { under18: true }
          })
          lastLoadedProfileTokenRef.current = sessionToken
          onLoadingChange(false)
          return
        }

        if (isNoLicenseEnrollment(profile, enrollmentSession)) {
          navigate('/thank-you', {
            replace: true,
            state: { noLicense: true }
          })
          lastLoadedProfileTokenRef.current = sessionToken
          onLoadingChange(false)
          return
        }

        const resolvedSessionStep = normalizeCurrentStepForUi(enrollmentSession)
        const fallbackProfileStep =
          typeof profile.currentStep === 'number'
            ? normalizeSelfEnrollmentEntryStep(profile.currentStep)
            : null
        const nextStep =
          typeof resolvedSessionStep === 'number' ? resolvedSessionStep : fallbackProfileStep

        if (typeof nextStep === 'number') {
          const returnedFromSelfEnroll = previousActivePath === '/self-enroll'
          const shouldStayOnApplication =
            shouldStayOnApplicationFromNavigation ||
            returnedFromSelfEnroll ||
            nextStep === SELF_ENROLLMENT_STEPS.ELIGIBILITY &&
              !hasCompleteApplicationEntryFields(profile)

          if (!shouldStayOnApplication && nextStep >= SELF_ENROLLMENT_STEPS.ELIGIBILITY) {
            setCurrentStep(nextStep)
            navigate('/self-enroll', {
              replace: true,
              state: { startStep: nextStep }
            })
          }
        }

        lastLoadedProfileTokenRef.current = sessionToken
        onLoadingChange(false)
      } catch (error) {
        if (!mounted) {
          return
        }

        onLoadingChange(false)
        onError(
          getEnrollmentApiErrorMessage(
            error,
            'We could not load your sponsored enrollment. Please refresh the page and try again.'
          )
        )
      }
    }

    void loadUserProfile()

    return () => {
      mounted = false
    }
  }, [
    activePath,
    navigate,
    onError,
    onLoadingChange,
    shouldStayOnApplicationFromNavigation,
    sessionToken,
    setCurrentStep,
    setEnrollmentId,
    setResumedEnrollment,
    setUserProfile
  ])
}
